import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface HeaderStructureProps {
  quillInstance: any;
}

interface HeaderItem {
  level: number;
  text: string;
  index: number;
}

export function HeaderStructure({ quillInstance }: HeaderStructureProps) {
  const [headers, setHeaders] = useState<HeaderItem[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (!quillInstance) return;

    const extractHeaders = () => {
      const editorRoot = quillInstance.root;
      const headerElements = editorRoot.querySelectorAll('h1, h2, h3, h4, h5, h6');
      
      const extractedHeaders: HeaderItem[] = [];
      
      headerElements.forEach((el: HTMLElement, index: number) => {
        const level = parseInt(el.tagName.substring(1), 10);
        const text = el.textContent || '';
        
        if (text.trim()) {
          extractedHeaders.push({
            level,
            text: text.trim(),
            index
          });
        }
      });
      
      setHeaders(extractedHeaders);
    };

    // Initial extraction
    extractHeaders();

    // Set up observer to watch for changes
    const observer = new MutationObserver(extractHeaders);
    
    observer.observe(quillInstance.root, {
      childList: true,
      subtree: true,
      characterData: true
    });
    
    return () => {
      observer.disconnect();
    };
  }, [quillInstance]);

  const scrollToHeader = (index: number) => {
    if (!quillInstance) return;
    
    const editorRoot = quillInstance.root;
    const headerElements = editorRoot.querySelectorAll('h1, h2, h3, h4, h5, h6');
    
    if (headerElements[index]) {
      headerElements[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const getIndentClass = (level: number) => {
    switch (level) {
      case 1: return 'ml-0';
      case 2: return 'ml-2';
      case 3: return 'ml-4';
      case 4: return 'ml-6';
      case 5: return 'ml-8';
      case 6: return 'ml-10';
      default: return 'ml-0';
    }
  };

  const getHeaderClass = (level: number) => {
    switch (level) {
      case 1: return 'text-sm font-bold text-gray-900';
      case 2: return 'text-sm font-semibold text-gray-800';
      case 3: return 'text-xs font-medium text-gray-700';
      case 4: return 'text-xs text-gray-600';
      case 5: return 'text-xs text-gray-500';
      case 6: return 'text-xs text-gray-400';
      default: return 'text-xs text-gray-600';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto">
          {headers.length > 0 ? (
            <div className="space-y-1">
              {headers.map((header, i) => (
                <div 
                  key={i} 
                  className={`${getIndentClass(header.level)} ${getHeaderClass(header.level)} cursor-pointer hover:text-indigo-600 transition-colors py-1`}
                  onClick={() => scrollToHeader(header.index)}
                >
                  <span className="text-gray-400 mr-1">H{header.level}:</span>
                  {header.text}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic p-2">No headers found in the document.</p>
          )}
        </div>
      )}
    </div>
  );
}